import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { FileText, Upload, ExternalLink, Ban, Eye } from 'lucide-react';
import PageHeader from '../../components/PageHeader';
import { crumbsFor } from '../../navigation/modules';
import api from '../../services/api';
import { useDialogs } from '../../components/Dialogs';
import { field, ghost, primary, muted, card, rowBorder, errorText } from '../time/styles';
import { Message } from '../time/ui';
import { LetterFrame } from './LettersTab';
import { DocStatus, Expiry } from './docUi';
import { viewUrl, kindLabel, fmtSize, extraFields, ACCEPT } from './docUtils';

// HR/Admin → Documents: issue letters from templates, upload documents for
// an employee, and see and manage everyone's documents.

const IssueLetter = ({ users, templates, onDone, say }) => {
    const [userId, setUserId] = useState('');
    const [templateId, setTemplateId] = useState('');
    const [extra, setExtra] = useState({});
    const [hidden, setHidden] = useState(false);
    const [preview, setPreview] = useState(null);
    const [busy, setBusy] = useState(false);
    const template = templates?.find(t => t.id === templateId);
    const extras = useMemo(() => extraFields(template), [template]);

    // The preview is of exactly what would be issued; changing anything clears it.
    useEffect(() => { setPreview(null); }, [userId, templateId, extra]);

    const showPreview = async () => {
        setBusy(true);
        try {
            const r = await api.post('/lifecycle/templates/preview', { kind: template.kind, name: template.name, subject: template.subject, body: template.body, userId, extra });
            setPreview(r.data);
        } catch (e) {
            say(false, errorText(e, 'Preview failed'));
        } finally {
            setBusy(false);
        }
    };

    const issue = async () => {
        setBusy(true);
        try {
            const r = await api.post('/lifecycle/documents/generate', { templateId, userId, extra, visibleToEmployee: !hidden });
            const who = users.find(u => u.id === userId)?.name;
            say(true, r.data.status === 'AWAITING_SIGNATURE' ? `Issued "${r.data.title}" to ${who}. They have been asked to sign it.` : `Issued "${r.data.title}" to ${who}.`);
            setPreview(null);
            setExtra({});
            onDone();
        } catch (e) {
            say(false, errorText(e, 'Could not issue the letter'));
        } finally {
            setBusy(false);
        }
    };

    if (!templates) return <p style={muted}>Loading letter templates…</p>;
    if (!templates.length) {
        return <p style={muted}>No letter templates are in use yet. Add them in <Link to="/admin/lifecycle?tab=letters">Lifecycle setup → Letters</Link>.</p>;
    }
    return (
        <div style={{ display: 'grid', gap: 12 }}>
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                <label style={{ display: 'grid', gap: 4 }}><span style={muted}>Employee</span>
                    <select aria-label="Employee for the letter" style={field} value={userId} onChange={e => setUserId(e.target.value)}>
                        <option value="">Choose…</option>
                        {users.map(u => <option key={u.id} value={u.id}>{u.name}{u.isActive === false ? ' (inactive)' : ''}</option>)}
                    </select>
                </label>
                <label style={{ display: 'grid', gap: 4 }}><span style={muted}>Letter</span>
                    <select aria-label="Letter template" style={field} value={templateId} onChange={e => { setTemplateId(e.target.value); setExtra({}); }}>
                        <option value="">Choose…</option>
                        {templates.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                    </select>
                </label>
            </div>
            {extras.length > 0 && (
                <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                    {extras.map(f => (
                        <label key={f.key} style={{ display: 'grid', gap: 4 }}><span style={muted}>{f.label}</span>
                            <input style={field} value={extra[f.key] || ''} maxLength={500} onChange={e => setExtra(prev => ({ ...prev, [f.key]: e.target.value }))} />
                        </label>
                    ))}
                </div>
            )}
            {template && (
                template.requiresSignature
                    ? <p style={{ ...muted, margin: 0 }}>The employee will be asked to sign this letter in the app.</p>
                    : <label style={{ display: 'flex', gap: 8, alignItems: 'center' }}><input type="checkbox" checked={hidden} onChange={e => setHidden(e.target.checked)} /><span>Keep it for HR only (don't show it to the employee)</span></label>
            )}
            <div style={{ display: 'flex', gap: 8 }}>
                <button style={ghost} disabled={!userId || !template || busy} onClick={showPreview}><Eye size={14} /> Preview</button>
                <button className="btn btn-primary" style={primary} disabled={!preview || preview.missing.length > 0 || busy} onClick={issue}><FileText size={14} /> Issue letter</button>
            </div>
            {preview?.missing?.length > 0 && <p role="alert" style={{ color: '#d97706', margin: 0 }}>Some fields have no value (highlighted). Fill them in above, or complete the employee's profile, then preview again.</p>}
            {preview && <LetterFrame html={preview.html} title="Letter to issue" />}
        </div>
    );
};

const UploadForm = ({ users, meta, onDone, say }) => {
    const empty = { userId: '', kind: 'ID_PROOF', title: '', expiresAt: '', hidden: false, file: null };
    const [f, setF] = useState(empty);
    const [busy, setBusy] = useState(false);
    const [inputKey, setInputKey] = useState(0);
    const set = (k, v) => setF(prev => ({ ...prev, [k]: v }));
    const kinds = [...meta.uploadKinds, ...Object.keys(meta.kindLabels)];

    const submit = async (e) => {
        e.preventDefault();
        if (f.file && f.file.size > 10 * 1024 * 1024) { say(false, 'The file must be under 10 MB'); return; }
        const data = new FormData();
        data.append('userId', f.userId);
        data.append('kind', f.kind);
        data.append('title', f.title);
        if (f.expiresAt) data.append('expiresAt', f.expiresAt);
        data.append('visibleToEmployee', f.hidden ? 'false' : 'true');
        data.append('file', f.file);
        setBusy(true);
        try {
            await api.post('/lifecycle/documents/upload', data, { headers: { 'Content-Type': 'multipart/form-data' } });
            say(true, `Uploaded "${f.title}"`);
            setF(empty);
            setInputKey(k => k + 1);
            onDone();
        } catch (err) {
            say(false, errorText(err, 'Could not upload the document'));
        } finally {
            setBusy(false);
        }
    };

    return (
        <form onSubmit={submit} style={{ display: 'grid', gap: 12 }}>
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                <label style={{ display: 'grid', gap: 4 }}><span style={muted}>Employee</span>
                    <select aria-label="Employee for the upload" required style={field} value={f.userId} onChange={e => set('userId', e.target.value)}>
                        <option value="">Choose…</option>
                        {users.map(u => <option key={u.id} value={u.id}>{u.name}{u.isActive === false ? ' (inactive)' : ''}</option>)}
                    </select>
                </label>
                <label style={{ display: 'grid', gap: 4 }}><span style={muted}>Kind</span>
                    <select aria-label="Document kind" style={field} value={f.kind} onChange={e => set('kind', e.target.value)}>
                        {kinds.map(k => <option key={k} value={k}>{kindLabel(meta, k)}</option>)}
                    </select>
                </label>
                <label style={{ display: 'grid', gap: 4, flex: '1 1 200px' }}><span style={muted}>Title</span>
                    <input aria-label="Document title" required maxLength={150} style={field} value={f.title} onChange={e => set('title', e.target.value)} placeholder="e.g. Passport" />
                </label>
                <label style={{ display: 'grid', gap: 4 }}><span style={muted}>Expires on (optional)</span>
                    <input type="date" aria-label="Expires on" style={field} value={f.expiresAt} onChange={e => set('expiresAt', e.target.value)} />
                </label>
            </div>
            <input key={inputKey} type="file" aria-label="File" accept={ACCEPT} required onChange={e => set('file', e.target.files[0] || null)} />
            <p style={{ ...muted, margin: 0 }}>PDF, PNG, JPEG or WebP, up to 10 MB. For Aadhaar, upload a masked copy (only the last 4 digits visible).</p>
            <label style={{ display: 'flex', gap: 8, alignItems: 'center' }}><input type="checkbox" checked={f.hidden} onChange={e => set('hidden', e.target.checked)} /><span>Keep it for HR only</span></label>
            <div><button className="btn btn-primary" style={primary} disabled={busy}><Upload size={14} /> {busy ? 'Uploading…' : 'Upload'}</button></div>
        </form>
    );
};

const DocumentsAdmin = () => {
    const { ask, dialogs } = useDialogs();
    const [message, setMessage] = useState(null);
    const [users, setUsers] = useState([]);
    const [templates, setTemplates] = useState(null); // null until loaded
    const [data, setData] = useState(null);
    const [params] = useSearchParams();
    const [filter, setFilter] = useState(() => ({ userId: params.get('userId') || '', status: '' }));
    const [panel, setPanel] = useState(null);
    const say = useCallback((ok, text) => setMessage({ ok, text }), []);

    const load = useCallback(() => {
        const params = Object.fromEntries(Object.entries(filter).filter(([, v]) => v));
        api.get('/lifecycle/documents', { params }).then(r => setData(r.data)).catch(e => say(false, errorText(e, 'Could not load documents')));
    }, [filter, say]);
    useEffect(() => { load(); }, [load]);
    useEffect(() => {
        api.get('/admin/users').then(r => setUsers(r.data)).catch(e => say(false, errorText(e, 'Could not load the employee list')));
    }, [say]);
    const loadTemplates = useCallback(() => {
        api.get('/lifecycle/templates').then(r => setTemplates(r.data.templates.filter(t => t.isActive))).catch(e => say(false, errorText(e, 'Could not load letter templates')));
    }, [say]);
    const openIssue = () => {
        if (panel === 'issue') { setPanel(null); return; }
        setTemplates(null);
        loadTemplates();
        setPanel('issue');
    };

    const revoke = async (doc) => {
        const reason = await ask({ title: `Withdraw "${doc.title}"?`, message: `For ${doc.user?.name}. It stays on record, marked as withdrawn.`, required: true, confirmText: 'Withdraw' });
        if (reason === null) return;
        try {
            await api.post(`/lifecycle/documents/${doc.id}/revoke`, { reason });
            say(true, `Withdrew "${doc.title}"`);
            load();
        } catch (e) {
            say(false, errorText(e, 'Could not withdraw the document'));
        }
    };

    const toggleVisible = async (doc) => {
        try {
            await api.patch(`/lifecycle/documents/${doc.id}`, { visibleToEmployee: !doc.visibleToEmployee });
            load();
        } catch (e) {
            say(false, errorText(e, 'Could not change who sees it'));
        }
    };

    const setExpiry = async (doc) => {
        const v = await ask({ title: `Expiry date for "${doc.title}"`, message: 'Leave it empty for no expiry.', label: 'Expiry date', type: 'date', defaultValue: doc.expiresAt || '', confirmText: 'Save' });
        if (v === null) return;
        try {
            await api.patch(`/lifecycle/documents/${doc.id}`, { expiresAt: v.trim() });
            load();
        } catch (e) {
            say(false, errorText(e, 'Could not change the expiry date'));
        }
    };

    const done = () => { setPanel(null); load(); };

    return (
        <div className="container" style={{ padding: '2rem 1rem', maxWidth: 1100 }}>
            {dialogs}
            <PageHeader crumbs={crumbsFor('hrDocuments')} />
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', flexWrap: 'wrap', gap: 12 }}>
                <div>
                    <h1 style={{ marginBottom: 4 }}>Documents</h1>
                    <p style={{ ...muted, fontSize: 15, margin: 0 }}>Letters issued to employees and the documents on their file.</p>
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                    <button className="btn btn-primary" style={primary} aria-expanded={panel === 'issue'} onClick={openIssue}><FileText size={14} /> Issue a letter</button>
                    <button style={ghost} aria-expanded={panel === 'upload'} onClick={() => setPanel(panel === 'upload' ? null : 'upload')}><Upload size={14} /> Upload a document</button>
                </div>
            </div>
            <Message message={message} />
            {panel === 'issue' && <section className="card" style={card} aria-label="Issue a letter"><IssueLetter users={users} templates={templates} onDone={done} say={say} /></section>}
            {panel === 'upload' && data && <section className="card" style={card} aria-label="Upload a document"><UploadForm users={users} meta={data} onDone={done} say={say} /></section>}

            <section className="card" style={card}>
                <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 12 }}>
                    <select aria-label="Filter by employee" style={field} value={filter.userId} onChange={e => setFilter(p => ({ ...p, userId: e.target.value }))}>
                        <option value="">Everyone</option>
                        {users.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
                    </select>
                    <select aria-label="Filter by status" style={field} value={filter.status} onChange={e => setFilter(p => ({ ...p, status: e.target.value }))}>
                        <option value="">Any status</option>
                        <option value="AWAITING_SIGNATURE">Awaiting signature</option>
                        <option value="SIGNED">Signed</option>
                        <option value="ISSUED">Issued</option>
                        <option value="REVOKED">Withdrawn</option>
                    </select>
                </div>
                {!data ? <p style={muted}>Loading…</p> : data.documents.length === 0 ? <p style={muted}>No documents yet.</p> : (
                    <div style={{ overflowX: 'auto' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14, color: 'var(--text-main)' }}>
                            <thead><tr style={{ textAlign: 'left', ...muted }}><th style={{ padding: 8 }}>Employee</th><th style={{ padding: 8 }}>Document</th><th style={{ padding: 8 }}>Status</th><th style={{ padding: 8 }}>Added</th><th /></tr></thead>
                            <tbody>
                                {data.documents.map(d => (
                                    <tr key={d.id} style={{ borderTop: rowBorder }}>
                                        <td style={{ padding: 8 }}>{d.user?.name}{d.user?.isActive === false && <span style={muted}> (inactive)</span>}</td>
                                        <td style={{ padding: 8 }}>
                                            <div>{d.title}</div>
                                            <div style={muted}>
                                                {kindLabel(data, d.kind)}{d.size ? ` · ${fmtSize(d.size)}` : ''}{d.uploadedBySelf ? ' · uploaded by the employee' : ''}{!d.visibleToEmployee ? ' · HR only' : ''}
                                            </div>
                                            <Expiry date={d.expiresAt} />
                                        </td>
                                        <td style={{ padding: 8 }}>
                                            <DocStatus doc={d} hrView />
                                            {d.signedAt && <div style={muted}>by {d.signedName}, {new Date(d.signedAt).toLocaleDateString()}</div>}
                                            {d.revokedReason && <div style={muted}>{d.revokedReason}</div>}
                                        </td>
                                        <td style={{ padding: 8, ...muted }}>{new Date(d.createdAt).toLocaleDateString()}</td>
                                        <td style={{ padding: 8 }}>
                                            <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                                                <a style={ghost} href={viewUrl(d.id)} target="_blank" rel="noopener noreferrer" aria-label={`Open ${d.title} for ${d.user?.name}`}><ExternalLink size={14} /> Open</a>
                                                {d.source === 'UPLOADED' && <button style={ghost} onClick={() => setExpiry(d)} aria-label={`Set expiry of ${d.title}`}>Expiry</button>}
                                                {d.status !== 'AWAITING_SIGNATURE' && d.status !== 'REVOKED' && (
                                                    <button style={ghost} onClick={() => toggleVisible(d)}>{d.visibleToEmployee ? 'Hide from employee' : 'Show to employee'}</button>
                                                )}
                                                {d.status !== 'REVOKED' && <button style={ghost} onClick={() => revoke(d)} aria-label={`Withdraw ${d.title} for ${d.user?.name}`}><Ban size={14} /> Withdraw</button>}
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </section>
        </div>
    );
};

export default DocumentsAdmin;
